import { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { buildWorksheetLoanFacts } from 'app/shared/modules/DocumentWorksheet/WorksheetDrawer/components/WorksheetLoanFactsPanel/WorksheetLoanFactsPanel.utils';

import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import { logger } from 'utils/logger';

import { getWorksheetLoanFactsAction } from 'store/documents/actions';
// Concrete module, not the store/loanApplications/selectors barrel (see WorksheetDrawer's imports).
import { selectIsChattelLoanPurposeFromApplication } from 'store/loanApplications/selectors/loanApplicationCommonInfo.selectors';

/**
 * Read-only loan facts shown beside a worksheet (LEN-132: the Pre-Funding Phone Audit interviewer
 * confirms them with the borrower) and printed with it, plus the raw loan responses they are built
 * from (LEN-194: a new worksheet's prefill reads its loan values from them). Chattel loans only — for
 * any other loan purpose, or while the drawer is closed, it reads nothing and returns no sections.
 *
 * @param {{ applicationId: string, isOpen: boolean }} params
 * @returns {{ sections: Array<{ id: string, title: string, rows: Array<{ label: string, value: string|null }> }>, loanFacts: Object|null }}
 */
export const useWorksheetLoanFacts = ({ applicationId, isOpen }) => {
  const dispatch = useDispatch();
  const isChattelLoan = useSelector(selectIsChattelLoanPurposeFromApplication);

  const [loanFacts, setLoanFacts] = useState(null);

  useEffectSafeStrictMode(() => {
    setLoanFacts(null);

    if (!isOpen || !isChattelLoan || !applicationId) {
      return;
    }

    // A reopen for another loan must not show the previous loan's late answer.
    const request = { isCancelled: false };

    dispatch(getWorksheetLoanFactsAction({ applicationId, silent: true }))
      .then(response => {
        if (!request.isCancelled) {
          setLoanFacts(response ?? null);
        }
      })
      .catch(logger.error);

    return () => {
      request.isCancelled = true;
    };
  }, [applicationId, isOpen, isChattelLoan, dispatch]);

  const sections = useMemo(
    () => (loanFacts ? buildWorksheetLoanFacts(loanFacts) : []),
    [loanFacts],
  );

  return useMemo(() => ({ sections, loanFacts }), [sections, loanFacts]);
};
